import { useState } from "react";
import {
  Tesoreria,
  Caso,
  Holding,
  Usuario,
  EventoEstado,
  Documento,
} from "../../types";
import { Badge, getEstadoTesoreriaBadgeVariant } from "../common/Badge";
import { TesoreriaDetail } from "./TesoreriaDetail";

interface TesoreriaViewProps {
  tesoreriaItems: Tesoreria[];
  casos: Caso[];
  holdings: Holding[];
  usuarios: Usuario[];
  eventosEstado: EventoEstado[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onTesoreriaUpdate: (tesoreria: Tesoreria) => void;
  onEventoEstadoCreate: (data: Omit<EventoEstado, "id">) => number;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
}

const ESTADO_TESORERIA_LABELS: Record<string, string> = {
  esperando_pago: "Esperando Pago SII",
  pago_recibido: "Pago Recibido",
  vencido: "Vencido",
  en_gestion: "En Gestion",
};

export function TesoreriaView({
  tesoreriaItems,
  casos,
  holdings,
  usuarios,
  eventosEstado,
  documentos,
  usuarioActual,
  onTesoreriaUpdate,
  onEventoEstadoCreate,
  onDocumentoCreate,
}: TesoreriaViewProps) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>("todos");

  const filtered = filtroEstado === "todos"
    ? tesoreriaItems
    : tesoreriaItems.filter((t) => t.estado === filtroEstado);

  const sorted = [...filtered].sort(
    (a, b) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime()
  );

  const selected = tesoreriaItems.find((t) => t.id === selectedId);
  const getHoldingNombre = (holdingId: number) =>
    holdings.find((h) => h.id === holdingId)?.nombre || "-";
  const getCasoTitulo = (solicitudId: number) =>
    casos.find((c) => c.id === solicitudId)?.titulo || "-";

  const formatMoney = (value?: number) =>
    value ? `$${value.toLocaleString("es-CL")}` : "-";

  // Check for overdue items (6+ months)
  const isOverdue = (item: Tesoreria) => {
    if (item.estado !== "esperando_pago") return false;
    const created = new Date(item.fechaCreacion);
    const sixMonths = new Date();
    sixMonths.setMonth(sixMonths.getMonth() - 6);
    return created < sixMonths;
  };

  return (
    <div className="flex h-full">
      {/* Left panel - list */}
      <div className="w-96 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">Tesoreria</h2>
          <p className="text-xs text-slate-500 mt-1">Seguimiento de pagos del SII</p>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="mt-2 w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
          >
            <option value="todos">Todos los estados</option>
            <option value="esperando_pago">Esperando Pago</option>
            <option value="pago_recibido">Pago Recibido</option>
            <option value="vencido">Vencido</option>
            <option value="en_gestion">En Gestion</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="p-4 text-sm text-slate-500 text-center">
              No hay items de tesoreria
            </div>
          ) : (
            sorted.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedId(item.id)}
                className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${
                  selectedId === item.id ? "bg-blue-50 border-l-2 border-l-blue-600" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-slate-800 truncate">
                    {getCasoTitulo(item.solicitudId)}
                  </span>
                  <Badge variant={getEstadoTesoreriaBadgeVariant(item.estado)}>
                    {ESTADO_TESORERIA_LABELS[item.estado] || item.estado}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  {getHoldingNombre(item.holdingId)}
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-slate-400">
                    {new Date(item.fechaCreacion).toLocaleDateString("es-CL")}
                  </span>
                  <span className="text-xs font-medium text-slate-600">
                    {formatMoney(item.montoPago)}
                  </span>
                </div>
                {isOverdue(item) && (
                  <div className="mt-1 text-xs text-red-600 font-medium">
                    Vencido (6+ meses)
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right panel - detail */}
      <div className="flex-1 overflow-hidden">
        {selected ? (
          <TesoreriaDetail
            tesoreria={selected}
            caso={casos.find((c) => c.id === selected.solicitudId)}
            holding={holdings.find((h) => h.id === selected.holdingId)}
            usuarios={usuarios}
            eventosEstado={eventosEstado.filter(
              (e) => e.entidadTipo === "tesoreria" && e.entidadId === selected.id
            )}
            documentos={documentos}
            usuarioActual={usuarioActual}
            onTesoreriaUpdate={onTesoreriaUpdate}
            onEventoEstadoCreate={onEventoEstadoCreate}
            onDocumentoCreate={onDocumentoCreate}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-slate-400">
            <p>Selecciona un item para ver su detalle</p>
          </div>
        )}
      </div>
    </div>
  );
}
